/**
 * @typedef {Object} ApiErrorBanner
 * @property {string} title
 * @property {string} [detail]
 */

const RATE_TITLE = "Too many requests right now";

const RATE_DETAIL =
  "GitHub limits how often this app can fetch data. Your connection and this app are fine—please wait a few minutes and try again.";

function looksLikeRateLimitMessage(text) {
  const s = String(text).toLowerCase();
  return (
    s.includes("rate limit") ||
    s.includes("abuse detection") ||
    s.includes("secondary rate") ||
    s.includes("too many requests")
  );
}

/**
 * Maps an axios error from the GitHub API into a calm, explanatory banner.
 * @param {import('axios').AxiosError} err
 * @returns {ApiErrorBanner}
 */
export function buildApiErrorFromAxios(err) {
  const status = err.response?.status;
  const data = err.response?.data;
  const dataMsg =
    typeof data === "object" && data !== null && typeof data.message === "string"
      ? data.message
      : "";
  const raw = dataMsg || err.message || "";

  if (status === 429) {
    return { title: RATE_TITLE, detail: RATE_DETAIL };
  }

  if (status === 403) {
    if (looksLikeRateLimitMessage(raw)) {
      return { title: RATE_TITLE, detail: RATE_DETAIL };
    }
    return {
      title: "Request could not be completed",
      detail: raw
        ? `${raw} If this keeps happening, try again later.`
        : "GitHub refused this request. Try again in a few minutes.",
    };
  }

  if (status === 422) {
    return {
      title: "Search could not run",
      detail: raw || "Adjust your filters or search terms and try again.",
    };
  }

  if (!err.response) {
    return {
      title: "Connection problem",
      detail: "We could not reach GitHub. Check your network and try again.",
    };
  }

  if (status >= 500) {
    return {
      title: "GitHub is temporarily unavailable",
      detail: "Please try again in a few minutes.",
    };
  }

  return {
    title: "Something went wrong",
    detail: raw || "Please try again.",
  };
}

/** @param {string} title @param {string} [detail] */
export function apiErrorBanner(title, detail) {
  return detail === undefined ? { title } : { title, detail };
}
